import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AlertTriangle, X, Shield, Clock, Timer } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { ImpersonationStatus } from '@bloom/shared';

/**
 * ImpersonationBanner Component
 * 
 * Displays a prominent banner when an admin is impersonating another user.
 * Shows:
 * - Clear warning that impersonation is active
 * - Target user information
 * - Session start time and inactivity timeout
 * - Stop impersonation button
 * 
 * Security & Audit:
 * - Banner is always visible - cannot be dismissed
 * - Bright warning colors for visibility
 * - All actions are logged on backend
 * - Auto-expires after 5 minutes of inactivity
 * - Ends session when app is closed
 */

// Inactivity timeout in milliseconds (5 minutes)
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;
// How often to send heartbeat to server (1 minute)
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
// How often to check for session expiry (10 seconds)
const CHECK_INTERVAL_MS = 10 * 1000;

interface ImpersonationBannerProps {
  onStop?: () => void;
}

export const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({ onStop }) => {
  const [status, setStatus] = useState<ImpersonationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [_lastActivity, setLastActivity] = useState<number>(Date.now()); // State triggers re-render, ref holds value
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number>(INACTIVITY_TIMEOUT_MS);
  
  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isStoppingRef = useRef<boolean>(false);

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!status?.isImpersonating) return;
    
    try {
      await apiClient.updateImpersonationActivity();
    } catch (error) {
      console.error('[ImpersonationBanner] Failed to send heartbeat:', error);
    }
  }, [status?.isImpersonating]);

  // Check if session should expire due to inactivity
  const checkExpiry = useCallback(async () => {
    if (!status?.isImpersonating || isStoppingRef.current) return;
    
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const remaining = INACTIVITY_TIMEOUT_MS - timeSinceActivity;
    
    setTimeUntilExpiry(Math.max(0, remaining));
    
    // If expired, stop the session
    if (timeSinceActivity >= INACTIVITY_TIMEOUT_MS) {
      console.log('[ImpersonationBanner] Session expired due to inactivity');
      isStoppingRef.current = true;
      toast.warning('Impersonation session expired due to inactivity');
      await handleForceStop();
    }
  }, [status?.isImpersonating]);

  // Force stop without confirmation (for timeout/unload)
  const handleForceStop = async () => {
    try {
      await apiClient.stopImpersonation();
    } catch (error) {
      // Session may already be expired — clear local state regardless
      console.error('[ImpersonationBanner] Failed to stop impersonation:', error);
    }

    clearImpersonationState();
  };

  // Load initial status
  const loadStatus = async () => {
    try {
      const response = await apiClient.getImpersonationStatus();
      if (response.success && response.data) {
        setStatus(response.data);
      }
    } catch (error) {
      console.error('[ImpersonationBanner] Failed to load impersonation status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set up activity listeners and intervals
  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (!status?.isImpersonating) return;

    // Activity event listeners
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle activity updates to once per second max
    let lastUpdate = 0;
    const throttledUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate > 1000) {
        lastUpdate = now;
        updateActivity();
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    // Heartbeat interval - send activity to server
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Check expiry interval
    checkIntervalRef.current = setInterval(checkExpiry, CHECK_INTERVAL_MS);

    // Handle page unload - try to end session
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Use sendBeacon for reliable async request during unload
      const token = localStorage.getItem('bloom_token');
      if (token) {
        // Try to stop impersonation via beacon
        navigator.sendBeacon('/api/impersonation/stop', JSON.stringify({}));
      }
      // Show confirmation dialog
      e.preventDefault();
      e.returnValue = 'You have an active impersonation session. Leaving will end it.';
      return e.returnValue;
    };

    const handleUnload = () => {
      // Cleanup on actual unload
      const token = localStorage.getItem('bloom_token');
      if (token) {
        navigator.sendBeacon('/api/impersonation/stop', JSON.stringify({}));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // Initial heartbeat
    sendHeartbeat();

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledUpdate);
      });
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [status?.isImpersonating, updateActivity, sendHeartbeat, checkExpiry]);

  // Shared cleanup: restore admin token and redirect
  const clearImpersonationState = () => {
    const adminToken = localStorage.getItem('bloom_admin_token');
    if (adminToken) {
      localStorage.setItem('bloom_token', adminToken);
      localStorage.removeItem('bloom_admin_token');
      localStorage.removeItem('bloom-auth');
      if (onStop) onStop();
      window.location.href = '/dashboard';
    } else {
      localStorage.removeItem('bloom_token');
      localStorage.removeItem('bloom_refresh_token');
      localStorage.removeItem('bloom-auth');
      if (onStop) onStop();
      window.location.href = '/login';
    }
  };

  const handleStopImpersonation = async () => {
    // Guard against double-clicks
    if (isStoppingRef.current || stopping) return;

    if (!confirm('Are you sure you want to stop impersonating this user? You will return to your admin account.')) {
      return;
    }

    setStopping(true);
    isStoppingRef.current = true;

    try {
      await apiClient.stopImpersonation();
      toast.success('Impersonation stopped. Returning to your admin account...');
    } catch (error: any) {
      // If 400, session already ended server-side — just clear local state
      if (error.response?.status === 400) {
        console.log('[ImpersonationBanner] Session already ended server-side, clearing local state');
      } else {
        toast.error(error.message || 'Failed to stop impersonation');
        setStopping(false);
        isStoppingRef.current = false;
        return;
      }
    }

    clearImpersonationState();
  };

  if (loading) {
    return null;
  }

  if (!status?.isImpersonating) {
    return null;
  }

  const sessionDuration = status.startTime
    ? Math.floor((Date.now() - new Date(status.startTime).getTime()) / 1000 / 60)
    : 0;

  const timeUntilExpiryMinutes = Math.ceil(timeUntilExpiry / 1000 / 60);
  const timeUntilExpirySeconds = Math.ceil((timeUntilExpiry / 1000) % 60);
  const isExpiryWarning = timeUntilExpiry < 60000; // Less than 1 minute

  return (
    <div className="bg-yellow-500 border-b-4 border-yellow-600 text-yellow-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-yellow-900" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5" />
                <p className="font-bold text-lg">
                  IMPERSONATION MODE ACTIVE
                </p>
              </div>
              
              <div className="mt-1 text-sm space-y-1">
                <p>
                  <span className="font-semibold">Viewing as:</span>{' '}
                  {status.targetUser?.name} ({status.targetUser?.email}) - {status.targetUser?.role}
                </p>
                <p>
                  <span className="font-semibold">Real admin:</span>{' '}
                  {status.actualUser?.name} ({status.actualUser?.email})
                </p>
                <div className="flex items-center space-x-4 text-yellow-800">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>Duration: {sessionDuration} min</span>
                  </div>
                  <div className={`flex items-center space-x-1 ${isExpiryWarning ? 'text-red-700 font-bold animate-pulse' : ''}`}>
                    <Timer className="h-4 w-4" />
                    <span>
                      {isExpiryWarning 
                        ? `Expires in ${timeUntilExpirySeconds}s!` 
                        : `Idle timeout: ${timeUntilExpiryMinutes}m`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleStopImpersonation}
              disabled={stopping}
              className="inline-flex items-center px-4 py-2 border-2 border-yellow-900 rounded-md shadow-sm text-sm font-bold text-yellow-900 bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="h-5 w-5 mr-2" />
              {stopping ? 'Stopping...' : 'Stop Impersonation'}
            </button>
          </div>
        </div>

        <div className="mt-2 text-xs text-yellow-800 italic">
          ⚠️ All actions are logged. Session auto-expires after 5 minutes of inactivity or when you close the app.
        </div>
      </div>
    </div>
  );
};

